import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtEUR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Mail, Phone, Factory, ShoppingCart, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useDetailNav } from "@/lib/overzichtNav";
import { OverzichtNav } from "@/components/OverzichtNav";

export default function LeverancierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nav = useDetailNav("leveranciers", id, "/leveranciers");
  const { user } = useAuth();
  const isAdmin = user?.rol === "admin";
  const [l, setL] = useState(null);
  const [orders, setOrders] = useState([]);

  const load = async () => {
    try {
      const [{ data }, { data: ords }] = await Promise.all([
        api.get(`/leveranciers/${id}`),
        api.get("/inkooporders", { params: { leverancier_id: id }}),
      ]);
      setL(data); setOrders(ords);
    } catch { toast.error("Niet gevonden"); navigate("/leveranciers"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { setL(null); }, [id]);

  const patch = async (field, value) => {
    try {
      const { data } = await api.patch(`/leveranciers/${id}`, { [field]: value });
      setL(prev => ({ ...prev, ...data })); toast.success("Opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const remove = async () => {
    if (!window.confirm("Leverancier verwijderen?")) return;
    await api.delete(`/leveranciers/${id}`);
    toast.success("Verwijderd"); navigate("/leveranciers");
  };

  const nieuweOrder = async () => {
    const { data } = await api.post("/inkooporders", { leverancier_id: id, regels: [] });
    navigate(`/inkooporders/${data.id}`);
  };

  if (!l) return <div className="text-muted-foreground">Laden…</div>;

  const txt = (field, label, placeholder = "") => (
    <div>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input defaultValue={l[field] || ""} placeholder={placeholder}
        onBlur={(e)=>{ if (e.target.value !== (l[field] || "")) patch(field, e.target.value); }}
        data-testid={`lev-${field}`} />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="lev-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate(nav.terug || "/leveranciers")} data-testid="back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Overzicht
          </Button>
          <OverzichtNav nav={nav} basePad="/leveranciers" testid="lev-nav" />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={nieuweOrder} data-testid="new-inkooporder-btn">
            <ShoppingCart className="h-4 w-4 mr-1" /> Plaats inkooporder
          </Button>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={remove} className="text-destructive hover:text-destructive" data-testid="lev-delete">
              <Trash2 className="h-4 w-4 mr-1" />Verwijderen
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-xl p-6 space-y-4 h-fit">
          <div className="flex items-center gap-3">
            <Factory className="h-6 w-6 text-primary" />
            <Input defaultValue={l.naam}
              onBlur={(e)=>{ if (e.target.value !== l.naam) patch("naam", e.target.value); }}
              data-testid="lev-naam"
              className="font-display text-2xl font-medium border-0 px-0 h-auto py-1 focus-visible:ring-0" />
          </div>
          <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border">
            {txt("email", "E-mail", "sales@…")}
            {txt("telefoon", "Telefoon")}
            {txt("contactpersoon", "Contactpersoon")}
            {txt("website", "Website")}
            {txt("adres", "Adres")}
            {txt("postcode", "Postcode")}
            {txt("plaats", "Plaats")}
            {txt("land", "Land")}
            <div>
              <Label className="text-xs text-muted-foreground">Betaaltermijn (dagen)</Label>
              <Input type="number" min="0" step="1" defaultValue={l.factuurtermijn ?? ""} placeholder="bijv. 30"
                onBlur={(e)=>{ const v = e.target.value.trim(); let nv = v === "" ? null : Math.max(0, Math.floor(Number(v)));
                  if (nv !== null && !Number.isFinite(nv)) nv = null;
                  if (nv !== (l.factuurtermijn ?? null)) patch("factuurtermijn", nv); }}
                data-testid="lev-factuurtermijn" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Notitie</Label>
            <Textarea defaultValue={l.notitie || ""} rows={3}
              onBlur={(e)=>{ if (e.target.value !== (l.notitie || "")) patch("notitie", e.target.value); }} />
          </div>
          <div className="flex gap-2 pt-3 border-t border-border">
            {l.email && <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline"><Mail className="h-3 w-3" />Mail</a>}
            {l.telefoon && <a href={`tel:${l.telefoon.replace(/\s/g,"")}`} className="inline-flex items-center gap-1 text-sm text-primary hover:underline"><Phone className="h-3 w-3" />Bel</a>}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border font-medium">Inkooporders ({orders.length})</div>
            {orders.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Nog geen inkooporders.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr><th className="text-left p-3">Nummer</th><th className="text-left p-3">Status</th><th className="text-right p-3">Regels</th><th className="text-right p-3">Totaal</th><th></th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {orders.map(o => (
                    <tr key={o.id} onClick={()=>navigate(`/inkooporders/${o.id}`)} className="hover:bg-secondary/40 cursor-pointer" data-testid={`lev-order-${o.id}`}>
                      <td className="p-3 font-mono">{o.nummer}</td>
                      <td className="p-3"><Badge variant={o.status === "verstuurd" ? "default" : "secondary"}>{o.status}</Badge></td>
                      <td className="p-3 text-right">{o.aantal_regels}</td>
                      <td className="p-3 text-right font-medium">{fmtEUR(o.totaal)}</td>
                      <td className="p-3 text-right text-muted-foreground"><ExternalLink className="h-4 w-4 inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-4 border-b border-border font-medium">Producten van deze leverancier ({(l.producten || []).length})</div>
            {(l.producten || []).length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">Nog geen producten gekoppeld. Vul "Leverancier" in op een product om hem hier te zien.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr><th className="text-left p-3">SKU</th><th className="text-left p-3">Naam</th><th className="text-left p-3">Groep</th><th className="text-right p-3">Inkoop</th></tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(l.producten || []).map(p => (
                    <tr key={p.id} onClick={()=>navigate(`/producten/${p.id}`)} className="hover:bg-secondary/40 cursor-pointer">
                      <td className="p-3 font-mono text-xs">{p.sku}</td>
                      <td className="p-3">{p.naam}</td>
                      <td className="p-3 text-muted-foreground">{p.productgroep || "—"}</td>
                      <td className="p-3 text-right">{fmtEUR(p.inkoopprijs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
